import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, Link } from "react-router-dom";

const navLinks = [
  { href: "#projects", label: "Projects", id: "projects" },
  { href: "#services", label: "Stack", id: "services" },
  { href: "#contact", label: "Contact", id: "contact" },
];

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const location = useLocation();
  const isHomePage = location.pathname === "/maxwell";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isHomePage) return;

    const sectionIds = ["about", "projects", "services", "contact"];
    const observers = sectionIds.map((id) => {
      const el = document.getElementById(id);
      if (!el) return null;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(id);
        },
        { rootMargin: "-40% 0px -55% 0px" }
      );
      observer.observe(el);
      return observer;
    });

    return () => observers.forEach((obs) => obs?.disconnect());
  }, [isHomePage]);

  const linkClass = (id) =>
    `rounded-md px-3 py-2 text-sm transition-all duration-200 ${
      activeSection === id
        ? "text-emerald-400 bg-emerald-500/10"
        : "text-gray-400 hover:text-white hover:bg-gray-800"
    }`;

  return (
    <nav
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-gray-900/90 backdrop-blur-md border-b border-gray-800/60 shadow-xl shadow-black/20"
          : "bg-gray-900"
      }`}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link
            to="/maxwell"
            className="text-emerald-400 font-semibold text-sm tracking-wide hover:text-emerald-300 transition-colors"
          >
            Emmanuel Maxwell
          </Link>

          <div className="hidden sm:flex items-center gap-1">
            <a
              href="https://medium.com/@codehouseinc"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white hover:bg-gray-800 rounded-md px-3 py-2 text-sm transition-all duration-200"
            >
              Blog
            </a>
            {isHomePage &&
              navLinks.map(({ href, label, id }) => (
                <a key={href} href={href} className={linkClass(id)}>
                  {label}
                </a>
              ))}
          </div>

          <button
            type="button"
            className="sm:hidden p-2 rounded-md text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
            aria-expanded={isOpen}
            onClick={() => setIsOpen(!isOpen)}
          >
            <span className="sr-only">Toggle menu</span>
            {isOpen ? (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="sm:hidden border-t border-gray-800 overflow-hidden"
          >
            <div className="px-4 py-3 space-y-1">
              <a
                href="https://medium.com/@chijioke.emma.maxwell"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-gray-400 hover:text-white hover:bg-gray-800 rounded-md px-3 py-2 text-sm transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Blog
              </a>
              {navLinks.map(({ href, label, id }) => (
                <a
                  key={href}
                  href={href}
                  className={`block ${linkClass(id)}`}
                  onClick={() => setIsOpen(false)}
                >
                  {label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Header;
